import { Formik, FormikHelpers } from "formik";
import React, { useContext, useMemo } from "react";
import { createFormData } from "../../../../api/FormDataFactory";
import { candidateAPI } from "../../../../api/candidate";
import { useCvpQueryClient } from "../../../../api/queryClient";
import { CvpFormButton } from "../../../../generic-components/buttons/CvpFormButton";
import { CvpFileUploadBasic } from "../../../../generic-components/common/CvpFileUploadBasic";
import { CvpFormErrors } from "../../../../generic-components/common/CvpFormErrors";
import {
  UnsavedChangesBlocker,
  UnsavedChangesHandlerContext,
  useUnsavedChangesBlockerId,
} from "../../../../generic-components/common/FormikUnsavedChangesBlocker";
import { useLocStore } from "../../../../localization";
import { LocContext } from "../../../../localization/LocContext";
import { getJobRedirectProps } from "../../../../url/getJobRedirectProps";
import { redirect } from "../../../../url/redirect";
import { useConsentsForCandidate } from "../../../../user/useConsentsForCandidate";
import { useDevice } from "../../../../utils/style/useDevice";
import { Consent5000 } from "../../../common/Consent5000";
import { handleFormSubmitError } from "../../formUtils/handleFormSubmitError";
import { ChosenAttachmentsList } from "../../formsUI/ChosenAttachmentsList";
import { SPForm } from "../../formsUI/SPForm";
import { SPFormikCheckbox } from "../../formsUI/SPFormikCheckbox";
import { SPFormikTextArea } from "../../formsUI/SPFormikTextArea";
import {
  ApplyForJobFormValues,
  useApplyForJobSchema,
} from "../useApplyForJobSchema";

import { useApplyWithProfileFormData } from "./useApplyWithProfileFormData";

interface ApplyWithProfileProps {
  requireCoverLetter: boolean;
  requireCv: boolean;
}

export const ApplyWithProfileForm = (props: ApplyWithProfileProps) => {
  const { requireCoverLetter, requireCv } = props;

  const { APPLY_FOR_JOB_VALIDATION } = useApplyForJobSchema(
    requireCoverLetter,
    requireCv
  );

  const L = useLocStore(LocContext.JOB);
  const { isMobile } = useDevice();

  const { hasMissing5000Consent } = useConsentsForCandidate();

  const cvpQueryClient = useCvpQueryClient();

  const initialValues: ApplyForJobFormValues = useMemo(() => {
    return {
      applicationText: "",
      consent: false,
      attachments: [],
    };
  }, []);

  const { isFetched, idRelatedResolved, typeRelatedResolved } =
    useApplyWithProfileFormData();

  const unsavedChangesId = useUnsavedChangesBlockerId();
  const unsavedChangesContext = useContext(UnsavedChangesHandlerContext);

  async function submitForm(
    values: ApplyForJobFormValues,
    helpers: FormikHelpers<ApplyForJobFormValues>
  ) {
    try {
      const carryFormData = new FormData();

      if (values.attachments && values.attachments.length > 0) {
        values.attachments.forEach((file) => {
          carryFormData.append("attachments", file);
        });
      }

      const formData = createFormData(
        {
          idRelated: idRelatedResolved,
          typeRelated: typeRelatedResolved,
          applicationText: values.applicationText,
          attachments: values.attachments,
          publicApplicantListIsOkay: true,
          triggerSystemEvent: true,
        },
        null,
        carryFormData
      );

      const headers = { "Content-Type": null };

      const result = await candidateAPI.applyWithProfileMultiAttachements({
        formData,
        headers,
      });
    } catch (err) {
      handleFormSubmitError(err, helpers);
      return;
    }

    cvpQueryClient.invalidateQueries();
    unsavedChangesContext.unregisterDirtyForm(unsavedChangesId); // unblock immediately (so we dont have to wait for rerender)

    redirect("applicationsent", {
      mergeQs: {},
      method: "replace",
    });
  }

  if (!isFetched) return null;

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount
      validationSchema={APPLY_FOR_JOB_VALIDATION}
      onSubmit={submitForm}
    >
      {(formik) => {
        return (
          <SPForm style={{ width: "100%" }}>
            <div className="form-sections-container">
              <div className="form-section">
                {requireCoverLetter && (
                  <SPFormikTextArea
                    name="applicationText"
                    required
                    maxLength={1010}
                    hideErrorMessage
                    style={{
                      minHeight: "180px",
                    }}
                  />
                )}
                {requireCv && (
                  <>
                    <CvpFileUploadBasic
                      style={{ width: isMobile ? "100%" : "400px" }}
                      onFileSelected={(evt) => {
                        const files = evt.files;
                        formik.setFieldValue("attachments", [
                          ...formik.values.attachments,
                          ...files,
                        ]);
                      }}
                      loading={formik.isSubmitting}
                      label={L.id("98eb22359a21").loc("Last opp vedlegg")}
                      multiple // allow multiple files
                    />

                    {formik.values.attachments &&
                      formik.values.attachments.length > 0 && (
                        <ChosenAttachmentsList
                          attachments={formik.values.attachments}
                          header={L.id("166f7bdcfdb9").loc("Valgte vedlegg:")}
                        />
                      )}
                  </>
                )}

                {hasMissing5000Consent && (
                  <div>
                    <Consent5000 />

                    <SPFormikCheckbox
                      name="consent5000"
                      label={L.id("1181d314d16e").loc(
                        "Jeg forstår og aksepterer teksten ovenfor"
                      )}
                      checkboxRight
                    />
                  </div>
                )}
              </div>
            </div>

            <CvpFormErrors />
            <UnsavedChangesBlocker id={unsavedChangesId} />
            <div className="form-button-container">
              <CvpFormButton
                label={L.id("a9838122fbaa").loc("Avbryt")}
                severity="danger"
                {...getJobRedirectProps(idRelatedResolved, typeRelatedResolved)}
                disabled={formik.isSubmitting}
              />
              <CvpFormButton
                label={L.id("dc57f6a76115").loc("Send søknad")}
                icon="pi pi-arrow-right"
                disabled={formik.isSubmitting}
                loading={formik.isSubmitting}
              />
            </div>
          </SPForm>
        );
      }}
    </Formik>
  );
};
